import React, { useState, useRef, useEffect } from 'react'
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react'

// Reusable date field: a button showing the chosen date that opens a month-browsable popover
// calendar. Paging months keeps its own state, so it never closes/navigates until you pick a day
// (the native <input type=date> got closed by background re-renders — Paul 2026-06-26). value/onChange
// use an ISO "YYYY-MM-DD" string, like the native input it replaces.
const todayStr = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
const fmtLabel = (s) => (s ? new Date(s + 'T00:00:00').toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) : '— pick a date —')

export default function DatePopover({ value, onChange, className = '', buttonClassName = '' }) {
  const [open, setOpen] = useState(false)
  const base = value || todayStr()
  const [vy, setVy] = useState(Number(base.slice(0, 4)))
  const [vm, setVm] = useState(Number(base.slice(5, 7)) - 1)
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return undefined
    // Re-sync the displayed month to the current value each time we open.
    const b = value || todayStr()
    setVy(Number(b.slice(0, 4))); setVm(Number(b.slice(5, 7)) - 1)
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
    const t = setTimeout(() => { document.addEventListener('mousedown', onDoc); document.addEventListener('keydown', onKey) }, 0)
    return () => { clearTimeout(t); document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey) }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  const first = new Date(Date.UTC(vy, vm, 1))
  const startDow = (first.getUTCDay() + 6) % 7 // Monday-first
  const days = new Date(Date.UTC(vy, vm + 1, 0)).getUTCDate()
  const monLabel = first.toLocaleDateString('en-ZA', { month: 'long', year: 'numeric', timeZone: 'UTC' })
  const step = (n) => { const d = new Date(Date.UTC(vy, vm + n, 1)); setVy(d.getUTCFullYear()); setVm(d.getUTCMonth()) }
  const iso = (d) => `${vy}-${String(vm + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
  const cells = []
  for (let i = 0; i < startDow; i++) cells.push(null)
  for (let d = 1; d <= days; d++) cells.push(d)

  return (
    <div ref={ref} className={`relative inline-block ${className}`}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-1.5 rounded-md border border-brand-border bg-white px-2 py-1.5 text-sm text-brand-ink hover:bg-brand-surface ${buttonClassName}`}>
        <CalendarDays size={14} className="text-brand-primary" /> {fmtLabel(value)}
      </button>
      {open && (
        <div className="absolute left-0 top-full z-50 mt-2 w-64 rounded-xl border border-brand-border bg-white p-3 shadow-lg">
          <div className="mb-2 flex items-center justify-between">
            <button type="button" onClick={() => step(-1)} aria-label="Previous month" className="rounded p-1 text-brand-muted hover:bg-brand-surface hover:text-brand-ink"><ChevronLeft size={16} /></button>
            <span className="text-sm font-semibold text-brand-ink">{monLabel}</span>
            <button type="button" onClick={() => step(1)} aria-label="Next month" className="rounded p-1 text-brand-muted hover:bg-brand-surface hover:text-brand-ink"><ChevronRight size={16} /></button>
          </div>
          <div className="mb-1 grid grid-cols-7 gap-0.5 text-center text-[10px] font-medium text-brand-muted">
            {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((d) => <div key={d}>{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-0.5">
            {cells.map((d, i) => {
              if (d === null) return <div key={`e${i}`} />
              const day = iso(d)
              const cls = day === value ? 'bg-brand-primary text-white'
                : day === todayStr() ? 'font-bold text-brand-primary hover:bg-brand-primary/10'
                  : 'text-brand-ink hover:bg-brand-primary/10'
              return <button key={day} type="button" onClick={() => { onChange(day); setOpen(false) }} className={`rounded py-1 text-xs ${cls}`}>{d}</button>
            })}
          </div>
        </div>
      )}
    </div>
  )
}
